// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getKnowledge, saveKnowledge } from '../../../api/knowledgeApi';
import { FormRow, SaveBar, Section, SectionRail } from './primitives';

/* Business knowledge — what the assistant knows about this business: greeting,
   voice persona, hours, service area, scheduling policies, FAQ, escalation.
   VERSIONED + APPEND-ONLY: saving creates a NEW version and never overwrites the
   old one. The version line makes that visible so edits read as safe, not
   destructive. Field JSON shapes match the agent-surface contract exactly. */

const SECTIONS = [
  { id: 'voice', label: 'Greeting & persona' },
  { id: 'hours', label: 'Business hours' },
  { id: 'area', label: 'Service area' },
  { id: 'policies', label: 'Scheduling policies' },
  { id: 'faq', label: 'FAQ' },
  { id: 'escalation', label: 'Escalation' },
];

const DAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'],
  ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday'],
];

const emptyDraft = () => ({
  greeting: '',
  voice_persona: '',
  // hours editable per-day; `closed` days are omitted from business_hours on save.
  hours: DAYS.reduce((acc, [k]) => {
    acc[k] = { open: '08:00', close: '17:00', closed: k === 'sat' || k === 'sun' };
    return acc;
  }, {}),
  zips: '',
  radius_miles: '',
  center_lat: '',
  center_lng: '',
  buffer_minutes: '',
  same_day_allowed: false,
  min_lead_hours: '',
  max_days_out: '',
  faq: [],
  escalation: [],
});

// Map a loaded is_current row → the editable draft.
const draftFromRow = (row) => {
  const d = emptyDraft();
  if (!row || row.present === false) return d;
  d.greeting = row.greeting || '';
  d.voice_persona = row.voice_persona || '';
  const bh = row.business_hours && typeof row.business_hours === 'object' ? row.business_hours : {};
  DAYS.forEach(([k]) => {
    const day = bh[k];
    if (day && (day.open || day.close)) {
      d.hours[k] = { open: day.open || '08:00', close: day.close || '17:00', closed: false };
    } else {
      d.hours[k] = { ...d.hours[k], closed: true };
    }
  });
  const sa = row.service_area && typeof row.service_area === 'object' ? row.service_area : {};
  d.zips = Array.isArray(sa.zips) ? sa.zips.join(', ') : '';
  d.radius_miles = sa.radius_miles ?? '';
  d.center_lat = sa.center?.lat ?? '';
  d.center_lng = sa.center?.lng ?? '';
  const sp = row.scheduling_policies && typeof row.scheduling_policies === 'object' ? row.scheduling_policies : {};
  d.buffer_minutes = sp.buffer_minutes ?? '';
  d.same_day_allowed = !!sp.same_day_allowed;
  d.min_lead_hours = sp.min_lead_hours ?? '';
  d.max_days_out = sp.max_days_out ?? '';
  d.faq = Array.isArray(row.faq) ? row.faq.map((f) => ({ q: f.q || '', a: f.a || '' })) : [];
  d.escalation = Array.isArray(row.escalation_rules)
    ? row.escalation_rules.map((r) => ({ when: r.when || '', action: r.action || '' }))
    : [];
  return d;
};

const numOrUndef = (v) => (v === '' || v == null ? undefined : Number(v));
const dropUndef = (obj) => {
  const out = { ...obj };
  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
};

// Assemble the draft → the exact server JSON shapes.
const draftToPayload = (d) => {
  const business_hours = {};
  DAYS.forEach(([k]) => {
    const day = d.hours[k];
    if (!day.closed) business_hours[k] = { open: day.open, close: day.close };
  });
  const zips = String(d.zips || '').split(',').map((z) => z.trim()).filter(Boolean);
  const service_area = dropUndef({
    zips,
    radius_miles: numOrUndef(d.radius_miles),
    center: (d.center_lat !== '' && d.center_lng !== '')
      ? { lat: Number(d.center_lat), lng: Number(d.center_lng) }
      : undefined,
  });
  const scheduling_policies = dropUndef({
    buffer_minutes: numOrUndef(d.buffer_minutes),
    same_day_allowed: !!d.same_day_allowed,
    min_lead_hours: numOrUndef(d.min_lead_hours),
    max_days_out: numOrUndef(d.max_days_out),
  });
  return {
    greeting: d.greeting,
    voice_persona: d.voice_persona,
    business_hours,
    service_area,
    scheduling_policies,
    faq: (d.faq || []).filter((f) => f.q.trim() || f.a.trim()).map((f) => ({ q: f.q, a: f.a })),
    escalation_rules: (d.escalation || []).filter((r) => r.when.trim() || r.action.trim())
      .map((r) => ({ when: r.when, action: r.action })),
  };
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

export default function BusinessKnowledgeTab() {
  const [draft, setDraft] = useState(emptyDraft());
  const [baseline, setBaseline] = useState(emptyDraft());
  const [meta, setMeta] = useState({ version: null, created_at: null, updated_by: '', present: false });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getKnowledge();
      const d = draftFromRow(data);
      setDraft(d);
      setBaseline(d);
      setMeta({
        version: data?.version ?? null,
        created_at: data?.created_at ?? null,
        updated_by: data?.updated_by ?? '',
        present: data?.present !== false && (data?.version != null || !!data),
      });
    } catch (e) {
      // 404 / not-live-yet → treat as no knowledge on file, not an error toast.
      const d = emptyDraft();
      setDraft(d);
      setBaseline(d);
      setMeta({ version: null, created_at: null, updated_by: '', present: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline]
  );

  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const setDay = (day, field, value) =>
    setDraft((p) => ({ ...p, hours: { ...p.hours, [day]: { ...p.hours[day], [field]: value } } }));

  const setFaq = (i, field, value) =>
    setDraft((p) => ({ ...p, faq: p.faq.map((f, idx) => (idx === i ? { ...f, [field]: value } : f)) }));
  const addFaq = () => setDraft((p) => ({ ...p, faq: [...p.faq, { q: '', a: '' }] }));
  const removeFaq = (i) => setDraft((p) => ({ ...p, faq: p.faq.filter((_, idx) => idx !== i) }));

  const setEsc = (i, field, value) =>
    setDraft((p) => ({ ...p, escalation: p.escalation.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)) }));
  const addEsc = () => setDraft((p) => ({ ...p, escalation: [...p.escalation, { when: '', action: '' }] }));
  const removeEsc = (i) => setDraft((p) => ({ ...p, escalation: p.escalation.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await saveKnowledge(draftToPayload(draft));
      toast.success(data?.version ? `Saved — now version ${data.version}` : 'Business knowledge saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save business knowledge');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="sv2-body"><div className="sv2-col"><p className="sv2-section-sub">Loading…</p></div></div>;
  }

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} />
      <div className="sv2-col">
        <div className="sv2-obj" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`sv2-pill ${meta.present ? 'info' : 'off'}`}>
            {meta.present && meta.version != null ? `Version ${meta.version}` : 'Not saved yet'}
          </span>
          <span className="sv2-obj-sub">
            {meta.present
              ? `Updated ${fmtWhen(meta.created_at)}${meta.updated_by ? ` by ${meta.updated_by}` : ''} · saving creates a new version — nothing is overwritten.`
              : 'Nothing on file yet. Your first save becomes version 1.'}
          </span>
        </div>

        <Section id="voice" title="Greeting & persona"
          sub="How the assistant introduces itself and the tone it uses.">
          <FormRow label="Greeting" help="The opening line customers hear/read first.">
            <textarea rows={2} value={draft.greeting}
              placeholder="Thanks for reaching out — how can I help you today?"
              onChange={(e) => set('greeting', e.target.value)} />
          </FormRow>
          <FormRow label="Voice persona" help="Tone and style — friendly, concise, professional, etc.">
            <textarea rows={3} value={draft.voice_persona}
              placeholder="Warm and efficient. Confirms details back. Never over-promises on timing."
              onChange={(e) => set('voice_persona', e.target.value)} />
          </FormRow>
        </Section>

        <Section id="hours" title="Business hours"
          sub="When the business takes calls and appointments. Closed days are simply left off.">
          <div className="sv2-obj">
            <div className="sv2-hours">
              <div className="sv2-hours-row head"><span>Day</span><span>Open</span><span>Close</span><span>Closed</span></div>
              {DAYS.map(([k, label]) => {
                const row = draft.hours[k];
                return (
                  <div className="sv2-hours-row" key={k}>
                    <span>{label}</span>
                    <input type="time" value={row.open} disabled={row.closed}
                      onChange={(e) => setDay(k, 'open', e.target.value)} />
                    <input type="time" value={row.close} disabled={row.closed}
                      onChange={(e) => setDay(k, 'close', e.target.value)} />
                    <label className="sv2-closed">
                      <input type="checkbox" checked={row.closed}
                        onChange={(e) => setDay(k, 'closed', e.target.checked)} /> Closed
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        <Section id="area" title="Service area"
          sub="Where you work — the assistant uses this to qualify or turn down jobs.">
          <FormRow label="ZIP codes" help="Comma-separated ZIPs you service.">
            <input type="text" value={draft.zips} placeholder="33901, 33903, 33990"
              onChange={(e) => set('zips', e.target.value)} />
          </FormRow>
          <FormRow label="Radius (miles)" help="Optional — how far from your base you'll travel.">
            <input type="number" min="0" step="1" value={draft.radius_miles ?? ''} placeholder="e.g. 25"
              onChange={(e) => set('radius_miles', e.target.value)} />
          </FormRow>
          <FormRow label="Center (lat / lng)" help="Optional — the point the radius measures from.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input type="number" step="any" value={draft.center_lat ?? ''} placeholder="Lat"
                onChange={(e) => set('center_lat', e.target.value)} />
              <input type="number" step="any" value={draft.center_lng ?? ''} placeholder="Lng"
                onChange={(e) => set('center_lng', e.target.value)} />
            </div>
          </FormRow>
        </Section>

        <Section id="policies" title="Scheduling policies"
          sub="The rules the assistant follows when it books.">
          <FormRow label="Buffer between jobs (minutes)" help="Travel/setup padding the scheduler leaves.">
            <input type="number" min="0" step="5" value={draft.buffer_minutes ?? ''} placeholder="e.g. 15"
              onChange={(e) => set('buffer_minutes', e.target.value)} />
          </FormRow>
          <FormRow label="Allow same-day booking" help="Whether the assistant can offer today.">
            <label className="sv2-closed">
              <input type="checkbox" checked={draft.same_day_allowed}
                onChange={(e) => set('same_day_allowed', e.target.checked)} /> Same-day allowed
            </label>
          </FormRow>
          <FormRow label="Minimum lead time (hours)" help="How far ahead a booking must be.">
            <input type="number" min="0" step="1" value={draft.min_lead_hours ?? ''} placeholder="e.g. 2"
              onChange={(e) => set('min_lead_hours', e.target.value)} />
          </FormRow>
          <FormRow label="Book at most … days out" help="How far into the future the assistant schedules.">
            <input type="number" min="1" step="1" value={draft.max_days_out ?? ''} placeholder="e.g. 60"
              onChange={(e) => set('max_days_out', e.target.value)} />
          </FormRow>
        </Section>

        <Section id="faq" title="FAQ"
          sub="Answers the assistant can give directly. Question and answer per row.">
          <div className="sv2-obj-list">
            {draft.faq.map((f, i) => (
              <div className="sv2-obj" key={i}>
                <FormRow label={`Question ${i + 1}`} help="">
                  <input type="text" value={f.q} placeholder="What's a question customers often ask?"
                    onChange={(e) => setFaq(i, 'q', e.target.value)} />
                </FormRow>
                <FormRow label="Answer" help="">
                  <textarea rows={2} value={f.a} placeholder="The answer the assistant should give."
                    onChange={(e) => setFaq(i, 'a', e.target.value)} />
                </FormRow>
                <div className="sv2-actions">
                  <button className="sv2-btn sm danger" onClick={() => removeFaq(i)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          <div className="sv2-actions">
            <button className="sv2-btn" onClick={addFaq}>+ Add a question</button>
          </div>
        </Section>

        <Section id="escalation" title="Escalation rules"
          sub="When the assistant should stop and hand off to a person.">
          <div className="sv2-obj-list">
            {draft.escalation.map((r, i) => (
              <div className="sv2-obj" key={i}>
                <FormRow label={`When ${i + 1}`} help="The situation that triggers a hand-off.">
                  <input type="text" value={r.when} placeholder="Customer is upset or asks for a manager"
                    onChange={(e) => setEsc(i, 'when', e.target.value)} />
                </FormRow>
                <FormRow label="Action" help="What to do — e.g. hand_to_human.">
                  <input type="text" value={r.action} placeholder="hand_to_human"
                    onChange={(e) => setEsc(i, 'action', e.target.value)} />
                </FormRow>
                <div className="sv2-actions">
                  <button className="sv2-btn sm danger" onClick={() => removeEsc(i)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          <div className="sv2-actions">
            <button className="sv2-btn" onClick={addEsc}>+ Add a rule</button>
          </div>
        </Section>
      </div>

      <SaveBar dirty={dirty} saving={saving}
        onSave={save}
        onDiscard={() => setDraft(JSON.parse(JSON.stringify(baseline)))} />
    </div>
  );
}
